.inspector {
  min-width: 0;
  min-height: 0;
  border: var(--border-default) solid var(--color-border-subtle);
  border-radius: var(--radius-surface);
  background: var(--color-bg-surface);
  overflow: hidden;
}

.dialog {
  display: grid;
  grid-template-rows: auto minmax(0, 1fr);
  height: 100%;
  min-height: 0;
  outline: none;
}

/* Modal overlay for the drawer; the full-screen view fills the viewport. */
.scrim {
  position: fixed;
  inset: 0;
  z-index: 40;
}

.drawerScrim {
  background: color-mix(in srgb, var(--color-bg-inverse) 45%, transparent);
}

.header {
  display: flex;
  align-items: center;
  gap: var(--space-3);
  padding: var(--space-3) var(--space-4);
  border-bottom: var(--border-default) solid var(--color-border-subtle);
}

.heading {
  display: grid;
  flex: 1;
  min-width: 0;
}

.kicker {
  margin: 0;
  color: var(--color-content-tertiary);
  font: var(--type-label);
  font-family: var(--font-mono);
  letter-spacing: 0.08em;
  text-transform: uppercase;
}

.title {
  display: -webkit-box;
  margin: 0;
  min-width: 0;
  overflow: hidden;
  overflow-wrap: anywhere;
  font: var(--type-h4);
  /* Long record names stay on two lines; the full value is in the details below. */
  -webkit-box-orient: vertical;
  -webkit-line-clamp: 2;
  line-clamp: 2;
}

.back {
  flex: none;
}

/*
 * One bounded scrolling area: the pane is as tall as its container (dock) or the dialog
 * (drawer, full screen), the header and tabs stay put, and only a body that is taller than
 * the room left scrolls.
 */
.dock {
  display: flex;
  flex-direction: column;
  height: 100%;
}

.inspector .tabs {
  flex: 1 1 0;
  min-height: 0;
  /* Columns shrink to the pane: an unbroken excerpt line scrolls inside its block instead. */
  grid-template-columns: minmax(0, 1fr);
  grid-template-rows: auto minmax(0, 1fr);
  align-content: stretch;
  gap: 0;
}

.inspector .panel {
  display: flex;
  flex-direction: column;
  min-height: 0;
}

.body {
  display: grid;
  grid-template-columns: minmax(0, 1fr);
  flex: 1 1 0;
  gap: var(--space-4);
  align-content: start;
  min-height: 0;
  padding: var(--space-4);
  overflow-y: auto;
}

.entityHeader {
  display: flex;
  align-items: center;
  gap: var(--space-2);
}

.entityId {
  font: var(--type-code);
  font-weight: 600;
  color: var(--color-content-secondary);
}

.sectionHeading {
  font: var(--type-h3);
  font-size: 1.15rem;
  letter-spacing: var(--tracking-h3);
  color: var(--color-content-primary);
  margin: 0;
  line-height: 1.3;
}

.truthGrid {
  display: flex;
  flex-wrap: wrap;
  gap: var(--space-2);
}

/* Short screens: tighter spacing so the whole summary fits without its own scrollbar. */
@media (max-height: 820px) {
  .body {
    gap: var(--space-3);
    padding: var(--space-3);
  }
}

/* Columns appear as the pane gets wider; long values and identifiers take the full row. */
.facts {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(min(100%, 12rem), 1fr));
  gap: var(--space-3) var(--space-5);
  margin: 0;
}

.facts .wide {
  grid-column: 1 / -1;
}

.facts code {
  font: var(--type-code);
  overflow-wrap: anywhere;
}

.facts div {
  display: grid;
  gap: var(--space-1);
}

.facts dt {
  color: var(--color-content-tertiary);
  font: var(--type-label);
}

.facts dd {
  margin: 0;
  color: var(--color-content-primary);
  font: var(--type-compact);
  overflow-wrap: anywhere;
}

.sentence {
  margin: 0;
  color: var(--color-content-primary);
  font: var(--type-compact);
  overflow-wrap: anywhere;
}

.reviewActionArea {
  display: grid;
  gap: var(--space-2);
  padding: var(--space-3);
  border: var(--border-default) solid var(--color-border-subtle);
  border-radius: var(--radius-control);
  background: var(--color-bg-subtle);
}

.actionPrompt {
  font: var(--type-label);
  font-weight: 600;
  color: var(--color-content-secondary);
  margin: 0;
}

.actionBtnRow {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: var(--space-2);
}

.undoArea {
  display: flex;
  flex-direction: column;
  gap: var(--space-1);
  margin-top: var(--space-2);
}

.undoNote {
  font: var(--type-label);
  font-size: 0.72rem;
  color: var(--color-content-tertiary);
}

.evidenceList {
  display: grid;
  gap: var(--space-2);
}

.evidenceListItem {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: var(--space-3);
  padding: var(--space-2) var(--space-3);
  border: var(--border-default) solid var(--color-border-subtle);
  border-radius: var(--radius-control);
  background: var(--color-bg-subtle);
}

.evidenceId {
  font: var(--type-code);
  font-weight: 600;
  color: var(--color-content-primary);
}

.unavailable {
  margin: 0;
  padding: var(--space-3);
  border: var(--border-default) dashed var(--color-border-default);
  border-radius: var(--radius-small);
  color: var(--color-content-secondary);
  font: var(--type-compact);
}

.note {
  margin: 0;
  color: var(--color-content-tertiary);
  font: var(--type-compact);
}

/* Docked pane (≥1440px and working pane keeps ≥640px). */

/* Evidence provenance & trace styling (FE-042) */
.provenanceTrail {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: var(--space-2);
  padding: var(--space-2) var(--space-3);
  border-radius: var(--radius-control);
  background: var(--color-bg-subtle);
  border: var(--border-default) solid var(--color-border-subtle);
  font: var(--type-compact);
  animation: traceHighlight var(--motion-standard) var(--motion-ease);
}

@keyframes traceHighlight {
  from {
    background-color: color-mix(in srgb, var(--color-interactive-default) 15%, transparent);
  }
  to {
    background-color: var(--color-bg-subtle);
  }
}

.trailStep {
  display: inline-flex;
  align-items: baseline;
  gap: var(--space-1);
}

.trailStepLabel {
  font: var(--type-label);
  font-size: 0.72rem;
  font-weight: 600;
  text-transform: uppercase;
  color: var(--color-content-tertiary);
}

.trailLink {
  all: unset;
  cursor: pointer;
  font: var(--type-code);
  font-weight: 600;
  color: var(--color-interactive-default);
  text-decoration: underline;
}

.trailLink:hover {
  color: var(--color-interactive-hover);
}

.trailLink:focus-visible {
  outline: 2px solid var(--color-focus-ring);
  outline-offset: 2px;
}

.trailCurrent {
  font: var(--type-code);
  font-weight: 600;
  color: var(--color-content-primary);
}

.trailValue {
  font: var(--type-code);
  color: var(--color-content-secondary);
}

.trailSeparator {
  color: var(--color-content-tertiary);
  user-select: none;
}

.verifiedHash {
  display: inline-flex;
  align-items: center;
  gap: var(--space-1);
  font: var(--type-code);
  word-break: break-all;
}

.hashStatus {
  font: var(--type-label);
  font-size: 0.72rem;
  font-weight: 600;
  color: var(--color-positive-content, #036838);
}

.transformationList {
  margin: 0;
  padding-left: var(--space-4);
  display: grid;
  gap: var(--space-1);
  font: var(--type-compact);
  color: var(--color-content-secondary);
}

.citingClaimsSection {
  display: grid;
  grid-template-columns: minmax(0, 1fr);
  gap: var(--space-2);
  padding: var(--space-3);
  border: var(--border-default) solid var(--color-border-subtle);
  border-radius: var(--radius-control);
  background: var(--color-bg-subtle);
}

.subHeading {
  margin: 0;
  font: var(--type-label);
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: var(--tracking-label);
  color: var(--color-content-secondary);
}

.citingList {
  display: grid;
  grid-template-columns: minmax(0, 1fr);
  gap: var(--space-2);
}

.citingItem {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  justify-content: space-between;
  gap: var(--space-2);
  padding: var(--space-2);
  border-radius: var(--radius-small);
  background: var(--color-bg-surface);
  border: var(--border-default) solid var(--color-border-subtle);
}

.citingItemLeft {
  display: flex;
  align-items: center;
  gap: var(--space-2);
  min-width: 0;
  flex: 1;
}

.citingTitle {
  font: var(--type-compact);
  color: var(--color-content-primary);
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.kindBadge {
  display: inline-flex;
  align-items: center;
  padding: 0 var(--space-2);
  min-height: 20px;
  border-radius: var(--radius-round);
  background: var(--color-bg-subtle);
  border: var(--border-default) solid var(--color-border-subtle);
  font: var(--type-label);
  font-weight: 600;
  color: var(--color-content-secondary);
}

.rawBadge {
  display: inline-flex;
  align-items: center;
  gap: var(--space-1);
  padding: 0 var(--space-2);
  min-height: 20px;
  border-radius: var(--radius-round);
  background: var(--color-bg-subtle);
  border: var(--border-default) solid var(--color-border-subtle);
  font: var(--type-label);
  font-weight: 600;
  color: var(--color-content-primary);
}

.aiBadge {
  display: inline-flex;
  align-items: center;
  gap: var(--space-1);
  padding: 0 var(--space-2);
  min-height: 20px;
  border-radius: var(--radius-round);
  background: var(--color-bg-inverse);
  border: var(--border-default) solid var(--color-border-strong);
  font: var(--type-label);
  font-weight: 600;
  color: var(--color-content-inverse);
}

/* Animations for Dock, Drawer, and Full presentation modes (FE-013, FE-052) */
.dock {
  display: grid;
  grid-template-columns: minmax(0, 1fr);
  grid-template-rows: auto minmax(0, 1fr);
  height: 100%;
  animation: inspectorDockGlide var(--motion-standard) var(--motion-ease);
}

@keyframes inspectorDockGlide {
  from {
    opacity: 0;
    transform: translateX(12px);
  }
  to {
    opacity: 1;
    transform: translateX(0);
  }
}

.drawer {
  position: fixed;
  top: 0;
  right: 0;
  bottom: 0;
  width: min(520px, 92vw);
  z-index: 40;
  border-radius: 0;
  box-shadow: var(--shadow-modal);
  animation: inspectorDrawerSlide var(--motion-standard) var(--motion-ease);
}

@keyframes inspectorDrawerSlide {
  from {
    transform: translateX(100%);
  }
  to {
    transform: translateX(0);
  }
}

.full {
  position: fixed;
  inset: 0;
  z-index: 40;
  border-radius: 0;
  animation: inspectorFullFade var(--motion-standard) var(--motion-ease);
}

@keyframes inspectorFullFade {
  from {
    opacity: 0;
    transform: translateY(8px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}

@media (prefers-reduced-motion: reduce) {
  .dock,
  .drawer,
  .full,
  .provenanceTrail {
    animation: none !important;
    transform: none !important;
  }
}

[data-motion='reduced'] .dock,
[data-motion='reduced'] .drawer,
[data-motion='reduced'] .full,
[data-motion='reduced'] .provenanceTrail {
  animation: none !important;
  transform: none !important;
}
